import { useEffect, useState } from "react";
import { UserPlus, X, Search, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, currentMonth, monthLabel, formatApiError } from "../../lib/api";
import { MonthNav } from "../../components/MonthNav";
import { AddPlayerToSession } from "../../components/AddPlayerToSession";
import { SessionsPanel } from "../../components/SessionsPanel";
import { SessionEditForm } from "../../components/SessionEditForm";
import { useTaxonomy } from "../../lib/useTaxonomy";
import { useAuth } from "../../auth/AuthContext";

const MARKS = [
  { v: "P", label: "P", cls: "bg-[#D4AF37] text-black" },
  { v: "A", label: "A", cls: "bg-red-500/80 text-white" },
];

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";

const ORDINAL = (d) => {
  const rem10 = d % 10, rem100 = d % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${d}th`;
  return `${d}${rem10 === 1 ? "st" : rem10 === 2 ? "nd" : rem10 === 3 ? "rd" : "th"}`;
};
const formatSessionDate = (isoStr) => {
  if (!isoStr) return "";
  const [y, m, d] = isoStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const weekday = dt.toLocaleDateString("en-IN", { weekday: "long", timeZone: "UTC" });
  const month = dt.toLocaleDateString("en-IN", { month: "long", timeZone: "UTC" });
  return `${weekday}, ${ORDINAL(d)} ${month} ${y}`;
};

const PlayerAttendanceLookup = ({ month }) => {
  const [query, setQuery] = useState("");
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setPlayers([]); return; }
    api.get("/admin/players").then((r) => {
      const q = query.toLowerCase();
      setPlayers(r.data.filter((p) =>
        `${p.player_id} ${p.name}`.toLowerCase().includes(q)).slice(0, 10));
    }).catch(() => {});
  }, [query]);

  const pick = async (p) => {
    setSelected(p);
    setPlayers([]);
    setQuery(p.name);
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/attendance/player/${p.player_id}`, { params: { month } });
      setDetail(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  const clear = () => { setSelected(null); setDetail(null); setQuery(""); };

  useEffect(() => {
    if (selected) pick(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5 mb-10" data-testid="player-lookup">
      <h2 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">Player Attendance Lookup — {monthLabel(month)}</h2>
      <div className="relative">
        <div className="flex items-center gap-2">
          <Search size={16} className="text-white/40 absolute left-3 pointer-events-none" />
          <input
            placeholder="Type player name or ID to search…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(null); setDetail(null); }}
            className={inputCls + " w-full pl-10"}
            data-testid="lookup-search"
          />
          {selected && (
            <button onClick={clear} className="text-white/50 hover:text-white text-xs uppercase tracking-widest border border-white/15 px-3 py-2" data-testid="lookup-clear">Clear</button>
          )}
        </div>
        {players.length > 0 && !selected && (
          <div className="absolute z-10 mt-1 w-full bg-[#0F0F0F] border border-white/10 max-h-64 overflow-y-auto" data-testid="lookup-results">
            {players.map((p) => (
              <button key={p.player_id} onClick={() => pick(p)}
                className="w-full text-left px-3 py-2.5 hover:bg-[#D4AF37]/10 border-b border-white/5 last:border-0"
                data-testid={`lookup-option-${p.player_id}`}>
                <p className="text-sm font-semibold">{p.name}</p>
                <p className="text-white/40 text-xs">{p.player_id} · {p.batch || "No batch"}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <p className="text-white/40 text-xs mt-4">Loading…</p>}

      {detail && (
        <div className="mt-6 pt-6 border-t border-white/10" data-testid="lookup-detail">
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Player</p>
              <p className="font-heading font-black">{detail.player.name}</p>
              <p className="text-[#D4AF37] text-xs">{detail.player.player_id}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Sessions Attended</p>
              <p className="font-heading text-3xl font-black text-[#D4AF37]" data-testid="lookup-present">{detail.present}</p>
              <p className="text-white/40 text-xs">of {detail.total} marked</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Absences</p>
              <p className="font-heading text-3xl font-black text-red-400" data-testid="lookup-absent">{detail.absent}</p>
            </div>
          </div>

          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Session Dates</p>
          {detail.records.length === 0 ? (
            <p className="text-white/40 text-sm border border-white/10 p-4">No sessions recorded for this player in {monthLabel(month)}.</p>
          ) : (
            <div className="space-y-1.5">
              {detail.records.map((r) => (
                <div key={r.date} className="flex items-center justify-between text-sm border border-white/5 bg-[#050505] px-4 py-2.5" data-testid={`lookup-record-${r.date}`}>
                  <div>
                    <p className="font-semibold">{new Date(r.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p>
                    <p className="text-white/40 text-xs">{r.batch}</p>
                  </div>
                  <span className={`w-8 h-8 flex items-center justify-center font-heading font-black text-xs rounded-sm ${r.mark === "P" ? "bg-[#D4AF37] text-black" : r.mark === "A" ? "bg-red-500/80 text-white" : "bg-blue-500/80 text-white"}`}>{r.mark}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Attendance() {
  const [month, setMonth] = useState(currentMonth());
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState("");
  const [coaches, setCoaches] = useState([]);
  const [coach, setCoach] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [roster, setRoster] = useState(null);
  const [addedPlayers, setAddedPlayers] = useState([]);
  const [trialRows, setTrialRows] = useState([]);
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showTrial, setShowTrial] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [trial, setTrial] = useState({ name: "", phone: "", year_of_birth: "", location: "", notes: "" });
  const { centreForBatch } = useTaxonomy();

  useEffect(() => {
    api.get("/staff/my-batches").then((r) => {
      setBatches(r.data.batches);
      if (r.data.batches.length && !batch) setBatch(r.data.batches[0]);
    }).catch(() => {});
    if (isAdmin) api.get("/admin/coaches").then((r) => setCoaches(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSessions = () => api.get("/admin/attendance/sessions", { params: { month } }).then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); }, [month]);

  const openSession = async (s) => {
    try {
      const { data } = await api.get("/admin/attendance/session", { params: { date: s.date, batch: s.batch } });
      setSessionDetail(data);
      setEditing(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteSession = async () => {
    if (!window.confirm(`Delete the whole ${sessionDetail.batch} session on ${formatSessionDate(sessionDetail.date)}? All ${sessionDetail.total} marks will be removed.`)) return;
    try {
      const { data } = await api.delete("/admin/attendance/session", { params: { date: sessionDetail.date, batch: sessionDetail.batch } });
      toast.success(`Session deleted (${data.deleted} mark${data.deleted === 1 ? "" : "s"} removed)`);
      setSessionDetail(null);
      loadSessions();
      if (roster && date === sessionDetail.date && batch === sessionDetail.batch) loadRoster();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const loadRoster = async (keepLocal = false) => {
    try {
      const { data } = await api.get("/admin/attendance/roster", { params: { batch, date } });
      setRoster(data.players);
      setAddedPlayers(data.added_players || []);
      setTrialRows(data.trials || []);
      if (keepLocal) {
        setMarks((prev) => ({ ...(data.marks || {}), ...prev }));
        setCoach((prev) => prev || data.coach || "");
        return;
      }
      setMarks(data.marks || {});
      setCoach(data.coach || "");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addTrial = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/trials", { ...trial, batch, date });
      toast.success(`${data.name} added to trial leads`);
      setTrialRows([...trialRows, data]);
      setMarks({ ...marks, [data.id]: "T" });
      setShowTrial(false);
      setTrial({ name: "", phone: "", year_of_birth: "", location: "", notes: "" });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        date, batch, centre: centreForBatch(batch), coach: coach || null,
        marks: Object.entries(marks).map(([player_id, mark]) => ({ player_id, mark })),
      };
      const { data } = await api.post("/admin/attendance", payload);
      toast.success(`Attendance saved for ${data.saved} players`);
      loadSessions();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div data-testid="admin-attendance">
      <div className="mb-8">
        <MonthNav month={month} onChange={setMonth} testid="att-month-nav" />
      </div>

      <h2 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">Mark Attendance for Your Session</h2>
      <div className="border border-white/10 bg-[#0A0A0A] p-5 mb-10">
        <div className={`grid gap-4 ${isAdmin ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2">Session Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls + " w-full text-base py-3.5"} data-testid="att-date" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2">Batch</label>
            <select value={batch} onChange={(e) => setBatch(e.target.value)} className={inputCls + " w-full text-base py-3.5"} data-testid="att-batch">
              {batches.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          {isAdmin && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-2">Coach</label>
              <select value={coach} onChange={(e) => setCoach(e.target.value)} className={inputCls + " w-full text-base py-3.5"} data-testid="att-coach">
                <option value="">— Select coach —</option>
                {coaches.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                {coach && !coaches.some((c) => c.name === coach) && <option value={coach}>{coach}</option>}
              </select>
            </div>
          )}
        </div>
        <button onClick={() => loadRoster()} className="mt-5 w-full sm:w-auto bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm px-10 py-4 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 gold-glow" data-testid="att-load">
          Load Roster
        </button>
      </div>

      {roster && (
        <div className="mb-12">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-white/40 text-xs uppercase tracking-widest" data-testid="att-count">{roster.length + addedPlayers.length + trialRows.length} players · {batch} · {date}</p>
            <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors duration-200" data-testid="att-add-player">
              <Plus size={14} /> Add Player to This Session
            </button>
          </div>
          {roster.length === 0 && addedPlayers.length === 0 && trialRows.length === 0 && <p className="text-white/40 text-sm border border-white/10 p-6">No active players in this batch.</p>}
          <div className="space-y-2">
            {roster.map((p) => (
              <div key={p.player_id} className="flex items-center justify-between gap-4 border border-white/10 bg-[#0A0A0A] p-4" data-testid={`att-row-${p.player_id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{p.name}</p>
                  <p className="text-[#D4AF37] text-xs">{p.player_id}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {MARKS.map((m) => (
                    <button key={m.v} onClick={() => setMarks({ ...marks, [p.player_id]: m.v })} data-testid={`att-mark-${p.player_id}-${m.v}`}
                      className={`w-14 h-11 font-heading font-black text-sm rounded-sm transition-all duration-150 ${marks[p.player_id] === m.v ? m.cls + " scale-110" : "border border-white/15 text-white/40 hover:border-white/40"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {addedPlayers.length > 0 && (
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold pt-4 pb-1" data-testid="att-added-header">Added to Session</p>
            )}
            {addedPlayers.map((p) => (
              <div key={`added-${p.player_id}`} className="flex items-center justify-between gap-4 border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-4" data-testid={`att-added-${p.player_id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{p.name} <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest ml-1">{p.status === "Active" ? "Guest" : "Returning"}</span></p>
                  <p className="text-white/40 text-xs">{p.player_id} · {p.centre || "No centre"}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {MARKS.map((m) => (
                    <button key={m.v} onClick={() => setMarks({ ...marks, [p.player_id]: m.v })} data-testid={`att-mark-${p.player_id}-${m.v}`}
                      className={`w-14 h-11 font-heading font-black text-sm rounded-sm transition-all duration-150 ${marks[p.player_id] === m.v ? m.cls + " scale-110" : "border border-white/15 text-white/40 hover:border-white/40"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {trialRows.length > 0 && (
              <p className="text-[10px] tracking-[0.25em] uppercase text-blue-300 font-semibold pt-4 pb-1">Trial Walk-Ins</p>
            )}
            {trialRows.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-4 border border-blue-400/30 bg-blue-400/5 p-4" data-testid={`att-trial-${t.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{t.name} <span className="text-blue-300 text-[10px] uppercase tracking-widest ml-1">Trial</span></p>
                  <p className="text-blue-300/70 text-xs">{t.phone}</p>
                </div>
                <span className="text-blue-300 text-[10px] uppercase tracking-widest">Marked Trial</span>
              </div>
            ))}
          </div>
          {(roster.length > 0 || addedPlayers.length > 0 || trialRows.length > 0) && (
            <button onClick={save} disabled={saving || Object.keys(marks).length === 0} className="mt-8 w-full sm:w-auto bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm px-10 py-4 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 disabled:opacity-50 gold-glow" data-testid="att-save">
              {saving ? "Saving..." : `Save Session (${Object.keys(marks).length} marked)`}
            </button>
          )}
        </div>
      )}

      <PlayerAttendanceLookup month={month} />

      <SessionsPanel sessions={sessions} month={month} onOpen={openSession} formatDate={formatSessionDate} />

      {sessionDetail && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto py-10 px-4" onClick={() => setSessionDetail(null)} data-testid="session-detail-modal">
          <div className="max-w-2xl mx-auto bg-[#0A0A0A] border border-white/10 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-5">
              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">{formatSessionDate(sessionDetail.date)}</p>
                <h2 className="font-heading text-xl font-black mt-1">{sessionDetail.batch}</h2>
                {sessionDetail.coach && <p className="text-white/60 text-xs mt-1" data-testid="session-detail-coach">Coach: {sessionDetail.coach}</p>}
                <p className="text-white/40 text-xs mt-1">
                  <span className="text-[#D4AF37]">{sessionDetail.present} present</span>
                  <span className="mx-2">·</span><span className="text-red-400">{sessionDetail.absent} absent</span>
                  {sessionDetail.trial > 0 && <><span className="mx-2">·</span><span className="text-blue-400">{sessionDetail.trial} trial</span></>}
                </p>
              </div>
              <div className="flex items-center gap-4">
                {!editing && sessionDetail.total > 0 && (
                  <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 px-3 py-2 hover:bg-[#D4AF37]/10 transition-colors duration-200" data-testid="session-edit-btn"><Pencil size={12} /> Edit</button>
                )}
                <button onClick={() => setSessionDetail(null)} className="text-white/50 hover:text-white" data-testid="session-detail-close"><X size={20} /></button>
              </div>
            </div>
            {editing ? (
              <SessionEditForm detail={sessionDetail} coaches={coaches} isAdmin={isAdmin} onCancel={() => setEditing(false)}
                onSaved={(d) => { loadSessions(); openSession({ date: d, batch: sessionDetail.batch }); }} />
            ) : (<>
            <div className="space-y-2">
              {sessionDetail.rows.map((r) => (
                <div key={r.player_id} className="flex items-center justify-between border border-white/5 bg-[#050505] px-4 py-2.5" data-testid={`session-row-${r.player_id}`}>
                  <p className="text-sm">{r.name}</p>
                  <span className={`w-8 h-8 flex items-center justify-center font-heading font-black text-xs rounded-sm ${r.mark === "P" ? "bg-[#D4AF37] text-black" : r.mark === "A" ? "bg-red-500/80 text-white" : "bg-blue-500/80 text-white"}`}>{r.mark}</span>
                </div>
              ))}
              {sessionDetail.rows.length === 0 && <p className="text-white/40 text-sm text-center py-6">No players marked in this session.</p>}
            </div>
            {isAdmin && sessionDetail.total > 0 && (
              <button onClick={deleteSession} className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest text-red-400/90 border border-red-400/40 px-4 py-2.5 hover:bg-red-500/10 transition-colors duration-200" data-testid="session-delete">
                <Trash2 size={13} /> Delete This Session
              </button>
            )}
            </>)}
          </div>
        </div>
      )}

      {showAdd && (
        <AddPlayerToSession
          batch={batch}
          centre={centreForBatch(batch)}
          date={date}
          coach={coach}
          onClose={() => setShowAdd(false)}
          onTrialPicked={() => { setShowAdd(false); setShowTrial(true); }}
          onExistingPicked={() => { setShowAdd(false); loadRoster(true); }}
          onReactivated={() => { setShowAdd(false); loadRoster(true); }}
        />
      )}

      {showTrial && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setShowTrial(false)} data-testid="trial-modal">
          <form onSubmit={addTrial} onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-8 w-full max-w-md">
            <div className="flex justify-between items-start mb-2">
              <h2 className="font-heading text-xl font-black">Trial Player Walk-In</h2>
              <button type="button" onClick={() => setShowTrial(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-white/50 text-sm mb-5">They'll be marked T for today's session and added to your Trial Leads tab for follow-up.</p>
            <div className="space-y-3">
              <input required placeholder="Player's name *" value={trial.name} onChange={(e) => setTrial({ ...trial, name: e.target.value })} className={inputCls + " w-full"} data-testid="trial-name" />
              <input required placeholder="Contact number *" value={trial.phone} onChange={(e) => setTrial({ ...trial, phone: e.target.value })} className={inputCls + " w-full"} data-testid="trial-phone" />
              <input placeholder="Year of Birth (e.g. 2014)" value={trial.year_of_birth} onChange={(e) => setTrial({ ...trial, year_of_birth: e.target.value })} className={inputCls + " w-full"} data-testid="trial-yob" />
              <input placeholder="Location / Area" value={trial.location} onChange={(e) => setTrial({ ...trial, location: e.target.value })} className={inputCls + " w-full"} data-testid="trial-location" />
              <textarea rows={2} placeholder="Notes (optional)" value={trial.notes} onChange={(e) => setTrial({ ...trial, notes: e.target.value })} className={inputCls + " w-full"} data-testid="trial-notes" />
            </div>
            <button type="submit" className="mt-5 w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="trial-save">Add & Mark Trial</button>
          </form>
        </div>
      )}
    </div>
  );
}
